import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { logActivity } from '../../lib/activityLog';
import { SupportOrgList } from './SupportHint';

/**
 * Small panel shown bottom-right after a user closes a record they either sat
 * with for a while or that was sensitive. Auto-dismisses after 20s, or can be
 * manually dismissed.
 *
 * Props:
 *   open, onClose
 *   orgs? — array of support org objects already resolved server-side
 *   reason? — 'sensitive' | 'long_read'
 */
export const GentleReturnPrompt = ({ open, onClose, orgs = [], reason }) => {
  const [showSupport, setShowSupport] = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade in/out + log shown event
  useEffect(() => {
    if (open) {
      setVisible(true);
      setShowSupport(false);
      logActivity('gentle_return_shown', { reason: reason || 'unknown' });
    } else {
      const t = setTimeout(() => setVisible(false), 250);
      return () => clearTimeout(t);
    }
  }, [open, reason]);

  // Auto-dismiss
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onClose?.(), 20000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!visible && !open) return null;

  const orgsToShow = (orgs || []).slice(0, 3);

  const handleDismiss = () => {
    logActivity('gentle_return_dismissed', { reason: reason || 'unknown' });
    onClose?.();
  };

  const handleShowSupport = () => {
    setShowSupport(true);
    logActivity('gentle_return_support_clicked', { reason: reason || 'unknown' });
  };

  const handleOrgClick = (org, type) => {
    logActivity('support_org_clicked', { organisation: org.name, category: type });
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed bottom-6 right-6 left-6 sm:left-auto sm:w-[400px] z-50 transition-[opacity,transform] duration-state ${
        open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1.5 pointer-events-none'
      }`}
      data-testid="gentle-return-prompt"
    >
      <div className="rounded-lg bg-care-wash shadow-overlay p-5 space-y-4">
        <div className="flex items-start gap-3">
          <p className="flex-1 text-[17px] leading-relaxed text-ink">
            You've been reading a while. You can stop and come back. Nothing is lost.
          </p>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-care-ink hover:text-ink rounded-md p-1 -m-1 flex-shrink-0"
            aria-label="Close"
            data-testid="gentle-return-dismiss"
          >
            <X className="w-4 h-4 stroke-[1.5]" />
          </button>
        </div>

        {showSupport && (
          orgsToShow.length > 0 ? (
            <SupportOrgList orgs={orgsToShow} onOrgClick={handleOrgClick} testId="gentle-return-orgs" />
          ) : (
            <p className="text-sm text-ink-70" data-testid="gentle-return-orgs">
              Samaritans listen at any hour: <a href="tel:116123" className="font-mono text-care-ink hover:underline">116 123</a>.
            </p>
          )
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {!showSupport && (
            <Button
              size="sm"
              variant="secondary"
              className="bg-surface hover:bg-surface/70"
              onClick={handleShowSupport}
              data-testid="gentle-return-show-support"
            >
              Show support
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-care-ink hover:text-care-ink hover:bg-surface/60"
            onClick={handleDismiss}
            data-testid="gentle-return-okay"
          >
            I'm okay
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GentleReturnPrompt;
